import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import { api, type SzamlaJelolt, type TulfizetesJeloltek, type TulfizetesSor } from '../api.ts';
import { ft } from '../formazas.ts';

/**
 * Túlfizetések a Számlák képernyőn. (A megrendelő kérése, 2026-09-11)
 *
 * Ami egy utalásból a számlák után megmaradt, mert a cégnek nem volt több
 * nyitott számlája, az a céghez került. Innen számlához párosítható: a pénz
 * a kijelölt számlák hátralékát fizeti, a legrégebbitől. A felhasználás
 * visszavonható. Túlfizetés a Bankkivonatok kézi párosításánál keletkezik.
 */
export function Tulfizetesek({ onValtozas }: { onValtozas?: () => void }) {
  const [lista, setLista] = useState<TulfizetesSor[] | null>(null);
  const [mind, setMind] = useState(false);
  const [nyitott, setNyitott] = useState<number | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);
  const [uzenet, setUzenet] = useState<string | null>(null);

  const betolt = useCallback(async () => {
    try {
      const v = await api.get<{ tulfizetesek: TulfizetesSor[] }>(
        `/api/bank/tulfizetesek?mind=${mind ? '1' : '0'}`,
      );
      setLista(v.tulfizetesek);
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A túlfizetések nem töltődtek be.');
    }
  }, [mind]);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  async function visszavon(k: TulfizetesSor['felhasznalasok'][number]) {
    if (
      !confirm(
        `Visszavonod a(z) ${k.bizonylatszam} számla kifizetését ebből a túlfizetésből (${ft(k.osszeg)} Ft)?\n\n` +
          'A számla újra nyitott (vagy részben fizetett) lesz, a túlfizetés maradéka visszanő.',
      )
    )
      return;
    try {
      await api.post(`/api/bank/tulfizetes/kifizetes/${k.kifizetes_id}/visszavonas`);
      setUzenet(`Visszavonva: ${k.bizonylatszam} — a számla újra nyitott.`);
      await betolt();
      onValtozas?.();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A visszavonás nem sikerült.');
    }
  }

  const felhasznalhato = (lista ?? []).reduce((a, t) => a + Math.max(0, t.maradek), 0);
  /** A cégnek túlfizetése van, miközben nyitott számlája is — ezeket érdemes párosítani. */
  const figyelendo = (lista ?? []).filter((t) => t.maradek > 2 && t.nyitott_szamlak > 0);

  return (
    <>
      <div className="cards">
        <div className={`card${felhasznalhato > 0 ? ' warn2' : ''}`}>
          <em>Felhasználható túlfizetés</em>
          <b>{ft(felhasznalhato)}</b>
          <span>Ft</span>
        </div>
      </div>

      {hiba && (
        <div className="uzenet bad" onClick={() => setHiba(null)}>
          {hiba}
        </div>
      )}
      {uzenet && (
        <div className="uzenet ok" onClick={() => setUzenet(null)}>
          {uzenet}
        </div>
      )}

      {figyelendo.length > 0 && (
        <div className="uzenet flag">
          <b>{figyelendo.length} túlfizetésnél</b> a cégnek nyitott számlája is van (összesen{' '}
          {ft(figyelendo.reduce((a, t) => a + t.nyitott_osszeg, 0))} Ft) — a „Párosítás…” gombbal a
          túlfizetésből kifizethető.
        </div>
      )}

      <div className="actbar">
        <span className="hint">
          Ami egy utalásból a számlák után megmaradt, mert a cégnek nem volt több nyitott számlája.
          A „Párosítás…” gombbal később számlához rendelhető. Túlfizetés a Bankkivonatok kézi
          párosításánál keletkezik.
        </span>
        <label className="hint" style={{ marginLeft: 'auto', whiteSpace: 'nowrap', cursor: 'pointer' }}>
          <input type="checkbox" checked={mind} onChange={(e) => setMind(e.target.checked)} /> a
          teljesen felhasználtak is
        </label>
      </div>

      <div className="matrix" style={{ maxHeight: 600, overflow: 'auto' }}>
        <table className="tt2">
          <thead>
            <tr>
              <th className="n" style={{ width: 92 }}>
                Beérkezett
              </th>
              <th>Cég</th>
              <th style={{ width: 240 }}>Befizető · közlemény</th>
              <th className="n" style={{ width: 96 }}>
                Túlfizetés
              </th>
              <th className="n" style={{ width: 96 }}>
                Felhasznált
              </th>
              <th className="n" style={{ width: 100 }}>
                Maradék
              </th>
              <th style={{ width: 150 }}>A cég nyitott számlái</th>
              <th style={{ width: 100 }}></th>
            </tr>
          </thead>
          <tbody>
            {(lista ?? []).map((t) => (
              <Fragment key={t.id}>
                <tr>
                  <td className="calc2">{t.datum}</td>
                  <td className="nm3">
                    <b>{t.cegnev}</b>
                  </td>
                  <td style={{ padding: '6px 10px', fontSize: 11.5 }}>
                    {t.partner_nev ?? '—'}
                    {t.kozlemeny && (
                      <div className="halvany" style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>
                        {t.kozlemeny}
                      </div>
                    )}
                  </td>
                  <td className="calc2">{ft(t.osszeg)}</td>
                  <td className="calc2">{t.felhasznalt > 0 ? ft(t.felhasznalt) : '—'}</td>
                  <td
                    className="calc2"
                    style={{ fontWeight: 600, color: t.maradek > 2 ? 'var(--flag)' : 'var(--ok)' }}
                  >
                    {t.maradek > 2 ? ft(t.maradek) : 'felhasználva'}
                  </td>
                  <td style={{ padding: '6px 10px', fontSize: 11.5 }}>
                    {t.nyitott_szamlak > 0 ? (
                      t.maradek > 2 ? (
                        <span
                          style={{ color: 'var(--flag)', fontWeight: 600 }}
                          title="A cégnek nyitott számlája is van — a túlfizetésből kifizethető"
                        >
                          van nyitott: {t.nyitott_szamlak} db · {ft(t.nyitott_osszeg)} Ft
                        </span>
                      ) : (
                        `${t.nyitott_szamlak} db · ${ft(t.nyitott_osszeg)} Ft`
                      )
                    ) : (
                      <span className="halvany">nincs</span>
                    )}
                  </td>
                  <td className="act">
                    <button
                      className={`btn sm${nyitott === t.id ? ' pri' : ''}`}
                      disabled={t.maradek <= 2}
                      title="A túlfizetés párosítása nyitott számlához"
                      onClick={() => setNyitott((n) => (n === t.id ? null : t.id))}
                    >
                      Párosítás…
                    </button>
                  </td>
                </tr>
                {t.felhasznalasok.map((k) => (
                  <tr key={`k${k.kifizetes_id}`}>
                    <td></td>
                    <td colSpan={5} style={{ padding: '4px 10px', fontSize: 11.5 }}>
                      ↳ kifizetve belőle: <b style={{ fontFamily: 'var(--mono)' }}>{k.bizonylatszam}</b>{' '}
                      · {ft(k.osszeg)} Ft
                      <span className="halvany"> · rögzítve {k.rogzitve.slice(0, 10)}</span>
                    </td>
                    <td></td>
                    <td className="act">
                      <button
                        className="btn sm"
                        title="A számla újra nyitott lesz, a túlfizetés maradéka visszanő"
                        onClick={() => void visszavon(k)}
                      >
                        Visszavonás
                      </button>
                    </td>
                  </tr>
                ))}
                {nyitott === t.id && (
                  <tr>
                    <td colSpan={8} style={{ padding: 0 }}>
                      <TulfizetesParosito
                        tulfizetes={t}
                        onMegse={() => setNyitott(null)}
                        onKesz={async (szoveg) => {
                          setNyitott(null);
                          setUzenet(szoveg);
                          await betolt();
                          onValtozas?.();
                        }}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {(lista === null || lista.length === 0) && (
              <tr>
                <td colSpan={8} style={{ padding: 26, textAlign: 'center', color: '#A3A9B0' }}>
                  {lista === null
                    ? 'Betöltés…'
                    : mind
                      ? 'Még nincs túlfizetés.'
                      : 'Nincs felhasználható túlfizetés.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Egy túlfizetés párosítása számlákhoz: elöl a cég nyitott számlái, fölöttük
 * kereső bármely más nyitott számlához. Alul látszik, mennyi a kijelöltek
 * hátraléka és mennyi marad a túlfizetésből.
 */
function TulfizetesParosito({
  tulfizetes,
  onKesz,
  onMegse,
}: {
  tulfizetes: TulfizetesSor;
  onKesz: (szoveg: string) => Promise<void>;
  onMegse: () => void;
}) {
  const [jeloltek, setJeloltek] = useState<SzamlaJelolt[] | null>(null);
  const [ismert, setIsmert] = useState<Map<number, SzamlaJelolt>>(new Map());
  const [kijelolt, setKijelolt] = useState<Set<number>>(new Set());
  const [kereses, setKereses] = useState('');
  const [talalatok, setTalalatok] = useState<SzamlaJelolt[]>([]);
  const [hiba, setHiba] = useState<string | null>(null);
  const [ment, setMent] = useState(false);

  useEffect(() => {
    api
      .get<TulfizetesJeloltek>(`/api/bank/tulfizetes/${tulfizetes.id}/jeloltek`)
      .then((j) => {
        setJeloltek(j.jeloltek);
        setIsmert(new Map(j.jeloltek.map((s) => [s.id, s])));
      })
      .catch((h: unknown) => setHiba(h instanceof Error ? h.message : 'A számlák nem tölthetők be.'));
  }, [tulfizetes.id]);

  useEffect(() => {
    const q = kereses.trim();
    if (q.length < 2) {
      setTalalatok([]);
      return;
    }
    const idozito = setTimeout(async () => {
      try {
        const v = await api.get<{ talalatok: SzamlaJelolt[] }>(
          `/api/bank/szamlak?q=${encodeURIComponent(q)}`,
        );
        setTalalatok(v.talalatok);
        setIsmert((m) => {
          const uj = new Map(m);
          for (const s of v.talalatok) uj.set(s.id, s);
          return uj;
        });
      } catch {
        setTalalatok([]);
      }
    }, 250);
    return () => clearTimeout(idozito);
  }, [kereses]);

  // A kijelöltek mindig látszanak, utánuk a cég számlái, végül a keresés találatai.
  const lista = useMemo(() => {
    const sorrend: SzamlaJelolt[] = [];
    const lattuk = new Set<number>();
    const tesz = (s: SzamlaJelolt | undefined) => {
      if (s && !lattuk.has(s.id)) {
        lattuk.add(s.id);
        sorrend.push(s);
      }
    };
    for (const id of kijelolt) tesz(ismert.get(id));
    for (const s of jeloltek ?? []) tesz(s);
    for (const s of talalatok) tesz(s);
    return sorrend;
  }, [kijelolt, ismert, jeloltek, talalatok]);

  const osszeg = [...kijelolt].reduce((a, id) => a + (ismert.get(id)?.hatralek ?? 0), 0);
  const marad = Math.round((tulfizetes.maradek - osszeg) * 100) / 100;

  function valt(id: number) {
    setKijelolt((k) => {
      const uj = new Set(k);
      if (uj.has(id)) uj.delete(id);
      else uj.add(id);
      return uj;
    });
  }

  async function rogzit() {
    setMent(true);
    setHiba(null);
    try {
      const v = await api.post<{ felhasznalt: number; maradek: number }>(
        `/api/bank/tulfizetes/${tulfizetes.id}/parositas`,
        { szamla_idk: [...kijelolt] },
      );
      await onKesz(
        `${ft(v.felhasznalt)} Ft felhasználva a(z) ${tulfizetes.cegnev} túlfizetéséből` +
          (v.maradek > 2 ? `, maradt ${ft(v.maradek)} Ft.` : '.'),
      );
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A párosítás nem sikerült.');
    } finally {
      setMent(false);
    }
  }

  return (
    <div
      style={{
        padding: '10px 12px',
        background: 'var(--bluew)',
        borderTop: '1.5px solid var(--blue)',
        borderBottom: '1.5px solid var(--blue)',
      }}
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <b>Túlfizetés párosítása</b>
        <span className="hint">
          Felhasználható: {ft(tulfizetes.maradek)} Ft. A pénz a kijelölt számlák hátralékát fizeti,
          a legrégebbitől; ha nem elég, a legújabb részben teljesül.
        </span>
        <input
          className="qi"
          style={{ width: 300, fontSize: 12.5, marginLeft: 'auto' }}
          placeholder="más számla keresése: szám, cégnév, telephely vagy összeg…"
          value={kereses}
          onChange={(e) => setKereses(e.target.value)}
        />
      </div>
      {hiba && <div className="uzenet bad">{hiba}</div>}

      <div className="matrix" style={{ marginTop: 8, maxHeight: 300, overflow: 'auto', background: '#fff' }}>
        <table className="tt2">
          <thead>
            <tr>
              <th style={{ width: 30 }}></th>
              <th style={{ width: 90 }}>Számla</th>
              <th>Cégnév / telephely</th>
              <th className="n" style={{ width: 92 }}>
                Kelt
              </th>
              <th className="n" style={{ width: 100 }}>
                Bruttó
              </th>
              <th className="n" style={{ width: 100 }}>
                Hátralék
              </th>
            </tr>
          </thead>
          <tbody>
            {lista.map((s) => (
              <tr
                key={s.id}
                style={{ cursor: s.nyitott ? 'pointer' : 'default', opacity: s.nyitott ? 1 : 0.55 }}
                onClick={() => s.nyitott && valt(s.id)}
              >
                <td style={{ textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={kijelolt.has(s.id)}
                    disabled={!s.nyitott}
                    onChange={() => valt(s.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>
                <td className="calc2" style={{ textAlign: 'left', fontWeight: 600 }}>
                  {s.bizonylatszam}
                </td>
                <td style={{ padding: '5px 10px', fontSize: 11.5 }}>
                  {s.cegnev ?? '—'}
                  {s.telephely_nev && <span className="halvany"> · {s.telephely_nev}</span>}
                </td>
                <td className="calc2">{s.kelt ?? '—'}</td>
                <td className="calc2">{ft(s.brutto)}</td>
                <td className="calc2" style={{ fontWeight: 600 }}>
                  {s.nyitott ? ft(s.hatralek) : '—'}
                </td>
              </tr>
            ))}
            {lista.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: 16, textAlign: 'center', color: 'var(--muted)' }}>
                  {jeloltek === null
                    ? 'Betöltés…'
                    : 'A cégnek nincs nyitott számlája — keress rá fent szám, név vagy összeg szerint.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
        <span>
          Kijelölve: <b>{kijelolt.size}</b> db ·{' '}
          <b style={{ fontFamily: 'var(--mono)' }}>{ft(osszeg)}</b> Ft
        </span>
        {kijelolt.size > 0 && (
          <span
            style={{
              color: Math.abs(marad) <= 2 * kijelolt.size ? 'var(--ok)' : 'var(--flag)',
              fontWeight: 600,
            }}
          >
            {Math.abs(marad) <= 2 * kijelolt.size
              ? 'pontosan kiadja a túlfizetést'
              : marad > 0
                ? `a túlfizetésből ${ft(marad)} Ft megmarad`
                : `a túlfizetés ${ft(-marad)} Ft-tal kevesebb — a legújabb számla részben teljesül`}
          </span>
        )}
        <span style={{ flex: 1 }}></span>
        <button className="btn pri sm" disabled={ment || kijelolt.size === 0} onClick={() => void rogzit()}>
          {ment ? 'Mentés…' : 'Párosítás rögzítése'}
        </button>
        <button className="btn sm" onClick={onMegse}>
          Mégse
        </button>
      </div>
    </div>
  );
}
